import { Card, Select, Spin } from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCandidateList, getJobList, updateCandidateStage, CANDIDATE_STAGES, stageIdToKey } from "../../redux/rtk/features/hiring/hiringSlice";
import "./HiringPipeline.css";

const STAGE_BORDER_COLORS = {
	APPLIED: "#1890ff",
	SCREENING: "#13c2c2",
	INTERVIEW: "#722ed1",
	OFFER: "#fa8c16",
	HIRED: "#52c41a",
	REJECTED: "#ff4d4f",
};

const HiringPipeline = () => {
	const dispatch = useDispatch();
	const [filterJobId, setFilterJobId] = useState(undefined);
	const [dragOverColumn, setDragOverColumn] = useState(null);
	const { candidateList, jobList, loading } = useSelector((state) => state.hiring);

	useEffect(() => {
		dispatch(getJobList());
	}, [dispatch]);

	useEffect(() => {
		dispatch(getCandidateList());
	}, [dispatch]);

	const handleStageChange = (record, newStageKey) => {
		const id = record.CandidateId ?? record.id ?? record.candidateId;
		dispatch(updateCandidateStage({ id, stage: newStageKey, record })).then(() => {
			dispatch(getCandidateList());
		});
	};

	const jobOptions = (jobList || []).map((j) => ({
		value: j.JobOpeningId ?? j.id ?? j.JobId,
		label: j.Title ?? j.title ?? j.JobTitle ?? j.jobTitle ?? `Job #${j.JobOpeningId ?? j.id ?? j.JobId}`,
	}));

	const rawCandidates = candidateList || [];
	const filteredCandidates = filterJobId != null ? rawCandidates.filter((c) => (c.JobOpeningId ?? c.jobOpeningId ?? c.jobId ?? c.JobId) === filterJobId) : rawCandidates;
	const candidatesByStage = filteredCandidates.reduce((acc, c) => {
		const key = c.StageId != null ? stageIdToKey(c.StageId) : String(c.stage ?? c.Stage ?? "APPLIED").toUpperCase();
		if (!acc[key]) acc[key] = [];
		acc[key].push(c);
		return acc;
	}, {});

	const getCandidateId = (c) => c.CandidateId ?? c.id ?? c.candidateId;
	const findCandidate = (candidateId) => filteredCandidates.find((c) => getCandidateId(c) === candidateId || getCandidateId(c) === Number(candidateId));

	const handleDragStart = (e, candidate, sourceStageKey) => {
		e.dataTransfer.setData(
			"application/json",
			JSON.stringify({ candidateId: getCandidateId(candidate), sourceStageKey })
		);
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/plain", ""); // for Firefox
	};

	const handleDragOver = (e, stageKey) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
		setDragOverColumn(stageKey);
	};

	const handleDragLeave = () => {
		setDragOverColumn(null);
	};

	const handleDrop = (e, targetStageKey) => {
		e.preventDefault();
		setDragOverColumn(null);
		try {
			const data = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
			const { candidateId, sourceStageKey } = data;
			if (!candidateId || sourceStageKey === targetStageKey) return;
			const candidate = findCandidate(candidateId);
			if (candidate) handleStageChange(candidate, targetStageKey);
		} catch (err) {
			// ignore
		}
	};

	return (
		<>
			<div className="flex justify-between items-center mb-4 flex-wrap gap-2">
				<span className="text-gray-600">Drag candidates between stages to update.</span>
				<Select
					placeholder="All jobs"
					allowClear
					style={{ width: 220 }}
					value={filterJobId}
					onChange={setFilterJobId}
					options={jobOptions}
				/>
			</div>
			{loading ? (
				<div className="flex justify-center py-8">
					<Spin size="large" />
				</div>
			) : (
				<div className="hiring-pipeline-columns">
					{CANDIDATE_STAGES.map((stageConfig) => {
						const list = candidatesByStage[stageConfig.key] ?? [];
						const isDropTarget = dragOverColumn === stageConfig.key;
						return (
							<div
								key={stageConfig.key}
								className={`hiring-pipeline-column ${isDropTarget ? "hiring-pipeline-column-drag-over" : ""}`}
								onDragOver={(e) => handleDragOver(e, stageConfig.key)}
								onDragLeave={handleDragLeave}
								onDrop={(e) => handleDrop(e, stageConfig.key)}
							>
								<div className="hiring-pipeline-column-header" style={{ borderColor: STAGE_BORDER_COLORS[stageConfig.key] || "#d9d9d9" }}>
									<span className="font-medium">{stageConfig.label}</span>
									<span className="hiring-pipeline-count">{list.length}</span>
								</div>
								<div className="hiring-pipeline-cards">
									{list.map((c) => {
										const id = getCandidateId(c);
										const first = c.FirstName ?? c.firstName ?? "";
										const middle = c.MiddleName ?? c.middleName ?? "";
										const last = c.LastName ?? c.lastName ?? "";
										const name = [first, middle, last].filter(Boolean).join(" ") || (c.name ?? c.Name ?? c.candidateName ?? "—");
										const jobTitle = c.JobTitle ?? c.jobTitle ?? c.JobOpeningTitle ?? c.job?.Title ?? "";
										return (
											<Card
												key={id}
												size="small"
												className="hiring-pipeline-card hiring-pipeline-card-draggable"
												draggable
												onDragStart={(e) => handleDragStart(e, c, stageConfig.key)}
											>
												<div className="font-medium">{name}</div>
												{jobTitle && <div className="text-gray-500 text-sm">{jobTitle}</div>}
											</Card>
										);
									})}
									{list.length === 0 && (
										<div className="hiring-pipeline-empty">{isDropTarget ? "Drop here" : "No candidates"}</div>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</>
	);
};

export default HiringPipeline;
